import { getCSS, useTranslation } from '@gimlite/common';
import { mapMode } from '@gimlite/common/stores/notification.store';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ColorType } from '../../types/component.type';
import { Icon, IconType } from '../icon/icon.component';
import { Text } from '../text/text.component';
import './toast.component.scss';
import { Spinner, SpinnerType } from '../spinner/spinner.component';

const variantMapper = {
  success: {
    color: 'alert_success',
    icon: 'check_fill',
    defaultTitle: 'watermelon-validated',
  },
  error: {
    color: 'alert_error',
    icon: 'alarm',
    defaultTitle: 'watermelon-error',
  },
  warning: {
    color: 'alert_warning',
    icon: 'alarm',
    defaultTitle: 'watermelon-warning',
  },
  info: {
    color: 'alert_info',
    icon: 'info2',
    defaultTitle: 'watermelon-info',
  },
  loading: {
    color: 'alert_info',
    spinner: 'squircle',
    defaultTitle: 'watermelon-loading',
  },
} as const satisfies Record<
  (typeof mapMode)[keyof typeof mapMode],
  | ({
      color: ColorType;
      defaultTitle: string;
    } & { icon?: IconType.Props['variant'] })
  | { spinner?: SpinnerType.Props['variant'] }
>;

export declare namespace ToastType {
  type Props = {
    className?: string | null;
    title?: string | null;
    description?: string | null;
    variant?: keyof typeof variantMapper;
  };
}

export const Toast = observer(
  ({ className, title, description, variant = 'success' }: ToastType.Props) => {
    const { t } = useTranslation();

    return (
      <div className={classNames('toast', className)}>
        <div
          style={{
            backgroundColor: getCSS(`--color-${variantMapper[variant].color}`),
          }}
          className="toast__border"
        ></div>

        <div className="toast__content">
          <Text variant="subhead" color={variantMapper[variant].color}>
            {title || t(variantMapper[variant].defaultTitle)}
          </Text>

          {description && (
            <Text variant="headcaption" color="deep_indigo">
              {description}
            </Text>
          )}
        </div>

        <div className="toast__icon">
          {'icon' in variantMapper[variant] ? (
            <Icon
              variant={variantMapper[variant].icon as IconType.Props['variant']}
              color={variantMapper[variant].color}
              size="s"
            />
          ) : (
            <Spinner
              variant={variantMapper[variant].spinner}
              size="small"
              color={variantMapper[variant].color}
            />
          )}
        </div>
      </div>
    );
  },
);
